import React from 'react';
import { Link } from 'react-router-dom';
import { Thumbnail } from '/src/components';
import {
    useCartContext,
    useStoreConfigContext,
    useModalsContext,
    useWishListsContext,
    useAuthContext, useMessagesContext,
} from '/src/providers';
import { PRODUCT_TYPES, STOCK_STATUSES } from '/src/consts';
import { IconSale, IconBasket } from './Icons';
import heart from './images/heart.png';
import heartEmpty from './images/heart-empty.png';
import * as styles from './styles.scss';

const ProductButton = ({ children, onClick, disabled }) => (
    <button
        className={styles.add}
        onClick={onClick}
        disabled={disabled}
        aria-label="Add product"
    >
        <IconBasket />
        {children}
    </button>
);

const ProductPrice = ({
    isConfigurable,
    translate,
    formatPrice,
    price: {
        minimum_price: {
            regular_price,
            final_price,
            discount: {
                percent_off,
                amount_off,
            } = {},
        } = {},
    }
}) => (
    <div className={styles.prices}>
        <div>
            {
                isConfigurable &&
                <span className={styles.from}>
                    {translate('From')}
                </span>
            }
            {
                !!regular_price &&
                <span className={styles.price}>
                    {`${formatPrice(!!percent_off ? final_price : regular_price)}`}
                </span>
            }
        </div>
        {
            !!final_price && !!percent_off &&
            <div className={styles.discounts}>
                <span className={styles.throw}>
                    {formatPrice(regular_price)}
                </span>
                <span className={styles.discount}>
                    {`-${formatPrice({ value: amount_off, currency: regular_price.currency })}`}
                </span>
            </div>
        }
    </div>
);

const ProductWishList = ({
    sku,
    isAuth,
    isAbsolute = true,
    successText,
    title,
}) => {
    const { showModal, MODALS_TYPES } = useModalsContext();
    const { isWishLists, isInWishLists, isLoadingWishLists } = useWishListsContext();
    const handleShowWishLists = () => showModal(MODALS_TYPES.WISH_LISTS, { sku, title });
    const isShowWishLists = isAuth && isWishLists && sku;

    return (
        isShowWishLists &&
        <button
            className={`${styles.lists}${isAbsolute ? ` ${styles.absolute}` : ''}`}
            onClick={handleShowWishLists}
            disabled={isLoadingWishLists}
            aria-label="Wish lists"
        >
            {
                isInWishLists(sku)
                ?   <>
                        <img src={heart} alt="Wish list" />
                        {successText && <span>{successText}</span>}
                    </>
                : <img src={heartEmpty} alt="Wish list" />
            }
        </button>
    )
};

const Product = ({
    productButton,
    variants,
    productPrice,
    onProductClick = f => f,
    product: {
        default_product_size_for_recipe,
        variants: productVariants,
        sku,
        type_id,
        name,
        url_key,
        stock_status,
        image: {
            url,
            label,
        } = {},
        price_range = {},
        price_range: {
            minimum_price: {
                discount: { percent_off: percent_off_mp } = {},
            } = {},
            promotion_price: {
                discount: { percent_off: percent_off_pp } = {},
            } = {}
        } = {},
    } = {},
    ...props
}) => {
    const {
        cart: {
            id: cartId
        },
        isLoadingCart,
        addProductsToCart,
    } = useCartContext();
    const {
        storeConfig: { product_url_suffix },
        translate,
        formatPrice,
    } = useStoreConfigContext();
    const { isAuth } = useAuthContext();
    const { showMessage, MESSAGES_TYPES } = useMessagesContext();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const ProductLinkWrapper = url_key ? Link : 'div';

    const isInStock = stock_status === STOCK_STATUSES.IN_STOCK;

    const handleAddProduct = () => addProductsToCart({
        isAuth,
        cartId,
        cartItems: [{ sku, quantity: 1 }]
    }).then(() => {
        showMessage(MESSAGES_TYPES.ADDED_PRODUCT_MESSAGE, { addedProducts: [sku] })
    }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }));

    const handleAddConfigurableProduct = () => showModal(MODALS_TYPES.CONFIGURABLE_PRODUCT, {
        sku,
        name,
        url_key,
        productVariants,
        defaultProductSizeForRecipe: default_product_size_for_recipe,
    });

    const handleAddByProductType = () => {
        if (type_id === PRODUCT_TYPES.SIMPLE || type_id === PRODUCT_TYPES.VIRTUAL) {
            return handleAddProduct;
        }

        if (type_id === PRODUCT_TYPES.CONFIGURABLE) {
            return handleAddConfigurableProduct;
        }

        return undefined;
    };

    return (
        <div
            className={styles.product}
            {...props}
        >
            <ProductLinkWrapper
                { ...(url_key ? { to:`/${url_key}${product_url_suffix}`,  'aria-label': name, onClick: onProductClick } : {}) }
            />
            {(!!percent_off_mp || !!percent_off_pp) && <IconSale className={styles.sale} />}
            <div className={`${!isInStock ? ` ${styles.disabled}` : ''}`}>
                <Thumbnail.Product>
                    {url && <img src={`${url}?d=400x360`} alt={label} />}
                </Thumbnail.Product>
            </div>
            <div className={`${styles.name}${!isInStock ? ` ${styles.disabled}` : ''}`}>
                {name}
            </div>
            {variants}
            <div className={`${styles.details}${!isInStock ? ` ${styles.disabled}` : ''}`}>
                {
                    productPrice ||
                    <ProductPrice
                        isConfigurable={type_id === PRODUCT_TYPES.CONFIGURABLE}
                        translate={translate}
                        formatPrice={formatPrice}
                        price={price_range}
                    />
                }
                {
                    !!percent_off_pp &&
                    <div className={styles.save}>
                        {`${translate('Save up to')} ${percent_off_pp}%`}
                    </div>
                }
                {
                    !percent_off_pp &&
                    <div className={styles.save}>
                        &nbsp;
                    </div>
                }
            </div>
            {
                productButton ||
                <ProductButton
                    onClick={handleAddByProductType()}
                    disabled={isLoadingCart || !isInStock}
                >
                    {isInStock ? translate('Add to basket') : translate('Out of stock')}
                </ProductButton>
            }
            <ProductWishList
                sku={sku}
                isAuth={isAuth}
                title={translate('Select your wishlist')}
            />
        </div>
    )
};

const ProductsWrapper = ({ children }) => (
    <div className={styles.wrapper}>
        {children}
    </div>
);

export { default as productGraphQLQueries } from './graph-ql-queries';
export {
    ProductPrice,
    ProductsWrapper,
    ProductButton,
    ProductWishList,
};

export default Product;
